import apiClient from "./api";

export interface Company {
  id: string;
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  userCount?: number;
  /**
   * Whether a tenant key is stored. The key itself is never sent back — it is a credential, it is
   * stored encrypted, and the edit form has nothing to echo. Write a new one through
   * {@link UpdateCompanyRequest}; sending an empty value leaves the stored key untouched.
   */
  hasOpenAiKey?: boolean;
  hasGeminiKey?: boolean;
  onboardingCompleted?: boolean;
  onboardingState?: string;
  memoryEnabled?: boolean;
  require2FA?: boolean;
}

export interface UpdateCompanyRequest {
  name?: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  openAiApiKey?: string;
  geminiApiKey?: string;
  memoryEnabled?: boolean;
}

export interface CompanyUser {
  id: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  status: "Active" | "Inactive" | "Pending" | "Expired";
  invitationId?: string;
}

export interface InviteUserRequest {
  email: string;
  role?: string;
}

export interface OnboardingState {
  currentStep: number;
  serverId?: string;
  widgetId?: string;
  docUrl?: string;
  docType?: string;
  serverName?: string;
  serverDescription?: string;
  authConfig?: any;
  selectedToolIds?: string[];
  companyWebsite?: string;
}

export const companyService = {
  getCompany: async (id: string): Promise<Company> => {
    const response = await apiClient.get(`/company/${id}`);
    return response.data;
  },

  updateCompany: async (
    id: string,
    data: UpdateCompanyRequest,
  ): Promise<Company> => {
    const response = await apiClient.put(`/company/${id}`, data);
    return response.data;
  },

  // Enable/disable the company-wide 2FA requirement (Admin only).
  update2faPolicy: async (
    id: string,
    require2FA: boolean,
  ): Promise<Company> => {
    const response = await apiClient.put(`/company/${id}/2fa-policy`, {
      require2FA,
    });
    return response.data;
  },

  // Reset a teammate's 2FA when they've lost their device (Admin only).
  reset2faForUser: async (userId: string): Promise<void> => {
    await apiClient.post(`/company/users/${userId}/2fa/reset`);
  },

  getCompanyUsers: async (): Promise<CompanyUser[]> => {
    const response = await apiClient.get("/company/users");
    return response.data;
  },

  inviteUser: async (data: InviteUserRequest): Promise<CompanyUser> => {
    const response = await apiClient.post("/company/users/invite", data);
    return response.data;
  },

  updateUserRole: async (
    userId: string,
    role: string,
  ): Promise<CompanyUser> => {
    const response = await apiClient.patch(`/company/users/${userId}/role`, {
      role,
    });
    return response.data;
  },

  toggleUserStatus: async (
    userId: string,
    isActive: boolean,
  ): Promise<CompanyUser> => {
    const response = await apiClient.patch(`/company/users/${userId}/status`, {
      isActive,
    });
    return response.data;
  },

  deleteUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/company/users/${userId}`);
  },

  resendInvitation: async (invitationId: string): Promise<void> => {
    await apiClient.post(`/company/invitations/${invitationId}/resend`);
  },

  cancelInvitation: async (invitationId: string): Promise<void> => {
    await apiClient.delete(`/company/invitations/${invitationId}`);
  },

  updateOnboardingState: async (state: OnboardingState): Promise<Company> => {
    const response = await apiClient.patch("/company/onboarding-state", {
      onboardingState: JSON.stringify(state),
    });

    // Update localStorage
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      user.company = response.data;
      localStorage.setItem("user", JSON.stringify(user));
    }

    return response.data;
  },

  completeOnboarding: async (): Promise<Company> => {
    const response = await apiClient.patch("/company/complete-onboarding");

    // Update localStorage
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      user.company = response.data;
      localStorage.setItem("user", JSON.stringify(user));
    }

    return response.data;
  },

  getCurrentCompany: (): Company | null => {
    const userStr = localStorage.getItem("user");
    if (!userStr) return null;

    try {
      const user = JSON.parse(userStr);
      return user.company || null;
    } catch {
      return null;
    }
  },
};
